import { useEffect, useState } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import type { DropiCatalogoProducto } from '@/types/dropi';
import type { Product } from '@/types/products';
import { useUpdateProduct } from './use-products';
import {
  useDropiProducto,
  useDropiProductos,
  useDropiStatus,
} from '../integrations/use-dropi';
import { formatNumber } from './products-format';

/**
 * Vincular un producto del catálogo con su equivalente en Dropi (docs/DROPI.md).
 *
 * Por qué existe: para crear la orden en Dropi hace falta el id del producto del PROVEEDOR y su
 * `user_id`. Eso no se puede adivinar por el nombre (el mismo producto puede existir varias veces
 * con proveedores y precios distintos), así que se elige a mano UNA vez y queda guardado.
 *
 * Sin este vínculo, el envío de la orden falla con el nombre del producto que falta vincular.
 */
export interface ProductDropiDialogProps {
  open: boolean;
  onClose: () => void;
  /** Producto del catálogo a vincular (`null` = diálogo cerrado). */
  product: Product | null;
}

const PAGINA = 30;

export function ProductDropiDialog({ open, onClose, product }: ProductDropiDialogProps) {
  const { tenantId, canEdit } = useActiveTenantAccess();
  const status = useDropiStatus(tenantId);
  const updateProduct = useUpdateProduct(tenantId);

  const [paginas, setPaginas] = useState(1);
  const [elegido, setElegido] = useState<DropiCatalogoProducto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const conectado = Boolean(status.data?.configurada && status.data?.activo);

  // Al abrir, se arranca por la primera página y sin selección (el vínculo actual se pinta aparte).
  useEffect(() => {
    if (!open) return;
    setPaginas(1);
    setElegido(null);
    setError(null);
    updateProduct.reset();
  }, [open, product?.id]);

  const vinculado = Boolean(product?.dropiProductId);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Producto en Dropi"
      description={product ? product.nombre : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={updateProduct.isPending}>
            Cerrar
          </Button>
          <Button
            loading={updateProduct.isPending}
            disabled={!canEdit || !conectado || !elegido || !product}
            onClick={() => void vincular()}
          >
            {vinculado ? 'Cambiar el vínculo' : 'Vincular producto'}
          </Button>
        </>
      }
    >
      {product && conectado ? (
        <div className="space-y-4">
          {vinculado ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-control border border-line bg-surface-muted px-3 py-2">
              <div className="text-caption text-ink-muted">
                <span className="font-medium text-ink">Vinculado</span> · Dropi #{product.dropiProductId}
                {product.dropiProveedor ? ` · ${product.dropiProveedor}` : ''}
                {product.dropiCosto ? ` · costo ${formatNumber(product.dropiCosto)}` : ''}
                {product.dropiBodega?.ciudad
                  ? ` · sale de ${product.dropiBodega.ciudad}`
                  : ''}
              </div>
              <Button
                variant="ghost"
                className="text-danger-strong hover:bg-danger-soft"
                disabled={!canEdit || updateProduct.isPending}
                onClick={() => void desvincular()}
              >
                Desvincular
              </Button>
            </div>
          ) : (
            <Alert tone="warning">
              Este producto no está vinculado con Dropi: la orden no se va a poder enviar hasta que
              elijas su equivalente en el catálogo de Dropi.
            </Alert>
          )}

          <ListaDropi
            key={`${product.id}-${open}`}
            tenantId={tenantId}
            paginas={paginas}
            elegido={elegido}
            onElegir={setElegido}
            onMas={() => setPaginas((p) => p + 1)}
          />

          {error ? <Alert tone="warning">{error}</Alert> : null}
        </div>
      ) : null}

      {product && !conectado ? (
        <Alert tone="warning">
          Primero conectá Dropi en Integraciones: sin token no se puede buscar en su catálogo.
        </Alert>
      ) : null}
    </Modal>
  );

  /** Guarda el vínculo con los datos del producto elegido (proveedor, bodega, costo y stock). */
  async function vincular() {
    if (!product || !elegido) return;
    setError(null);

    const bodega = elegido.warehouse_product?.[0];
    const ciudad = bodega?.warehouse?.city ?? null;
    const costo = Number(elegido.sale_price ?? 0) || null;

    await updateProduct
      .mutateAsync({
        productId: product.id,
        payload: {
          dropiProductId: elegido.id,
          dropiSupplierId: elegido.user?.id ?? null,
          dropiWarehouseId: bodega?.warehouse_id ?? null,
          dropiProveedor: elegido.user?.store_name ?? elegido.user?.name ?? null,
          // La BODEGA de origen viaja en el catálogo: se guarda para poder avisar antes de enviar si
          // Dropi no opera desde esa ciudad (docs/DROPI.md).
          dropiBodega:
            ciudad || bodega?.warehouse?.name
              ? {
                  nombre: bodega?.warehouse?.name ?? null,
                  ciudad: ciudad?.name ?? null,
                  codDane: ciudad?.cod_dane ?? null,
                  rateType: ciudad?.rate_type ?? null,
                  trajectoryType: ciudad?.trajectory_type ?? null,
                }
              : null,
          dropiCosto: costo,
          dropiStock: bodega?.stock ?? null,
        },
      })
      .then(() => {
        setElegido(null);
        onClose();
      })
      .catch((e: unknown) => setError(getErrorMessage(e)));
  }

  /** Desvincula: el backend limpia el vínculo completo (proveedor, costo, stock y variantes). */
  async function desvincular() {
    if (!product) return;
    setError(null);

    await updateProduct
      .mutateAsync({ productId: product.id, payload: { dropiProductId: null } })
      .catch((e: unknown) => setError(getErrorMessage(e)));
  }
}

/**
 * Lista de productos del catálogo de Dropi, con «cargar más».
 *
 * El API de integraciones no tiene búsqueda por texto (se verificó: solo pagina), así que el texto
 * que escribe el admin filtra lo YA cargado y, si no encuentra nada, se le ofrece cargar más.
 */
interface ListaDropiProps {
  tenantId: string | null;
  paginas: number;
  elegido: DropiCatalogoProducto | null;
  onElegir: (producto: DropiCatalogoProducto) => void;
  onMas: () => void;
}

function ListaDropi({ tenantId, paginas, elegido, onElegir, onMas }: ListaDropiProps) {
  const [items, setItems] = useState<DropiCatalogoProducto[]>([]);
  const [buscar, setBuscar] = useState('');
  /** Id que se pidió traer directo del catálogo (sin paginar). `null` = nadie lo pidió todavía. */
  const [idPedido, setIdPedido] = useState<number | null>(null);

  const pagina = useDropiProductos(tenantId, (paginas - 1) * PAGINA);
  const porId = useDropiProducto(tenantId, idPedido);

  useEffect(() => {
    if (!pagina.data) return;
    setItems((prev) => {
      const ids = new Set(prev.map((p) => p.id));
      return [...prev, ...pagina.data.filter((p) => !ids.has(p.id))];
    });
  }, [pagina.data]);

  // El producto que se pidió por id se elige solo: el id identifica UN producto y su proveedor, que
  // es justo lo que el admin acaba de pedir. Confirmar sigue siendo el botón «Vincular producto».
  useEffect(() => {
    if (porId.data) onElegir(porId.data);
  }, [porId.data]);

  const idDirecto = idDesdeTexto(buscar);
  const texto = buscar.trim().toLowerCase();
  const traido = porId.data ?? null;

  // El que se trajo por id se pinta ARRIBA y se saca de la lista para no verlo dos veces.
  const visibles = (
    texto
      ? items.filter((p) => p.name.toLowerCase().includes(texto) || String(p.id).includes(texto))
      : items
  ).filter((p) => p.id !== traido?.id);

  return (
    <div className="space-y-2">
      <Field label="Buscar en el catálogo de Dropi" htmlFor="dropiBuscar">
        <Input
          id="dropiBuscar"
          value={buscar}
          placeholder="Nombre, o el id de Dropi (ej. 2293650)"
          onChange={(evento) => setBuscar(evento.target.value)}
          onKeyDown={(evento) => {
            // Enter con un id pegado = traer ese producto, sin tener que buscar el botón.
            if (evento.key === 'Enter' && idDirecto) {
              evento.preventDefault();
              setIdPedido(idDirecto);
            }
          }}
        />
      </Field>

      {/* Vía rápida: el negocio casi siempre tiene el id (lo copia del panel o del link de Dropi) y el
          API de Dropi no busca por texto, así que pedirlo por id evita recorrer páginas. */}
      {idDirecto ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            loading={porId.isFetching}
            onClick={() => setIdPedido(idDirecto)}
          >
            Traer el producto #{idDirecto} de Dropi
          </Button>
          <span className="text-caption text-ink-faint">
            {porId.isFetching ? 'Consultando Dropi…' : 'Enter también lo trae'}
          </span>
        </div>
      ) : null}

      {porId.isError ? <Alert tone="warning">{getErrorMessage(porId.error)}</Alert> : null}

      <div className="max-h-72 space-y-1 overflow-y-auto rounded-control border border-line p-1">
        {pagina.isPending && items.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-6 text-caption text-ink-muted">
            <Spinner /> Buscando en Dropi…
          </div>
        ) : null}

        {traido ? (
          <FilaDropi
            producto={traido}
            activo={elegido?.id === traido.id}
            etiqueta="Traído por id"
            onElegir={onElegir}
          />
        ) : null}

        {visibles.map((p) => (
          <FilaDropi key={p.id} producto={p} activo={elegido?.id === p.id} onElegir={onElegir} />
        ))}

        {!pagina.isPending && visibles.length === 0 && !traido ? (
          <p className="px-2 py-6 text-center text-caption text-ink-muted">
            {items.length === 0
              ? 'Dropi no devolvió productos en esta página.'
              : 'Ninguno de los productos cargados coincide. «Cargar más» trae otros 30, o pegá el id de Dropi.'}
          </p>
        ) : null}
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="text-caption text-ink-faint">{items.length} productos cargados</span>
        <Button variant="secondary" size="sm" loading={pagina.isFetching} onClick={onMas}>
          Cargar más
        </Button>
      </div>
    </div>
  );
}

/** Una fila del catálogo: la usan la lista paginada y el producto traído por id. */
function FilaDropi({
  producto: p,
  activo,
  etiqueta,
  onElegir,
}: {
  producto: DropiCatalogoProducto;
  activo: boolean;
  /** Marca de dónde salió (`Traído por id`); se omite en las filas de la lista. */
  etiqueta?: string;
  onElegir: (producto: DropiCatalogoProducto) => void;
}) {
  const bodega = p.warehouse_product?.[0];
  const ciudad = bodega?.warehouse?.city?.name ?? null;

  return (
    <button
      type="button"
      onClick={() => onElegir(p)}
      className={`w-full rounded-control px-2 py-2 text-left transition-colors ${
        activo ? 'bg-brand-50' : 'hover:bg-surface-muted'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-body text-ink">{p.name}</span>
        <span className="text-caption whitespace-nowrap text-ink-muted">
          {formatNumber(Number(p.sale_price ?? 0))}
        </span>
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-ink-faint">
        <span>#{p.id}</span>
        {p.user?.store_name ? <span>· {p.user.store_name}</span> : null}
        {typeof bodega?.stock === 'number' ? <span>· stock {bodega.stock}</span> : null}
        {/* La ciudad de la bodega es el ORIGEN del despacho: sin ella Dropi no puede generar la guía
            (docs/DROPI.md), así que se muestra al elegir el producto. */}
        {ciudad ? <span>· sale de {ciudad}</span> : null}
        {p.type === 'VARIABLE' ? <Badge tone="info">Con variantes</Badge> : null}
        {etiqueta ? <Badge tone="success">{etiqueta}</Badge> : null}
      </div>
    </button>
  );
}

/**
 * El id de Dropi que el admin escribió o pegó: el número pelado (`2293650`), con `#` delante o el
 * link del panel (`https://app.dropi.co/products/2293650`, `.../productos/2293650`).
 *
 * Devuelve `null` cuando el texto NO es un id, y entonces el buscador sigue filtrando por nombre.
 * Se exporta para poder probarlo sin navegador (smoke de Productos).
 */
export function idDesdeTexto(texto: string): number | null {
  const limpio = texto.trim().replace(/^#/, '');
  if (/^\d{3,}$/.test(limpio)) return Number(limpio);

  const link = limpio.match(/(?:productos?|products?)\/(\d{3,})/i);
  return link ? Number(link[1]) : null;
}
